import { useState, useEffect, useRef } from 'react';

interface TestStats {
  clicks: number;
  errors: number;
  warnings: number;
  elapsed: number;
  duration: number;
  status: 'ready' | 'running' | 'stopped' | 'completed';
}

interface ConsoleLog {
  timestamp: string;
  level: 'info' | 'warn' | 'error';
  message: string;
}

export function StressTest() {
  const [duration, setDuration] = useState(10); // Default 10 minutes
  const [stats, setStats] = useState<TestStats>({
    clicks: 0,
    errors: 0,
    warnings: 0,
    elapsed: 0,
    duration: 10,
    status: 'ready',
  });
  const [logs, setLogs] = useState<ConsoleLog[]>([]);
  const [showControlPanel, setShowControlPanel] = useState(true);

  const testIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const elapsedIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );
  const startTimeRef = useRef<number | null>(null);

  const addLog = (level: ConsoleLog['level'], message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs((prev) => [...prev, { timestamp, level, message }]);
  };

  const simulateClick = () => {
    try {
      // Find all markers (AdvancedMarker renders as div elements with role="button")
      const markers = document.querySelectorAll('[role="button"]');

      if (markers.length === 0) {
        addLog('warn', 'No markers found on map');
        return;
      }

      // Pick a random marker
      const randomIndex = Math.floor(Math.random() * markers.length);
      const marker = markers[randomIndex] as HTMLElement;

      // Simulate click
      marker.click();

      setStats((prev) => ({ ...prev, clicks: prev.clicks + 1 }));
      addLog('info', `Clicked marker ${randomIndex + 1}/${markers.length}`);

      // Sometimes close the details panel (50% chance)
      if (Math.random() > 0.5) {
        setTimeout(
          () => {
            const closeBtn = document.querySelector(
              '[aria-label="Close"]'
            ) as HTMLElement;
            if (closeBtn) {
              closeBtn.click();
              addLog('info', 'Closed details panel');
            }
          },
          500 + Math.random() * 1000
        );
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      addLog('error', `Click simulation error: ${errorMessage}`);
      setStats((prev) => ({ ...prev, errors: prev.errors + 1 }));
    }
  };

  const scheduleNextClick = () => {
    const delay = 2000 + Math.random() * 2000; // 2-4 seconds
    testIntervalRef.current = setTimeout(() => {
      simulateClick();

      // Check if we should stop (based on selected duration)
      const elapsed =
        (Date.now() - (startTimeRef.current || Date.now())) / 1000;
      const maxSeconds = duration * 60; // Convert minutes to seconds
      if (elapsed < maxSeconds) {
        scheduleNextClick();
      } else {
        stopTest('completed');
        addLog('info', `=== TEST COMPLETED (${duration} minutes) ===`);
      }
    }, delay);
  };

  const startTest = () => {
    startTimeRef.current = Date.now();
    setStats({
      clicks: 0,
      errors: 0,
      warnings: 0,
      elapsed: 0,
      duration,
      status: 'running',
    });
    setLogs([]);

    addLog('info', '=== STRESS TEST STARTED ===');
    addLog('info', `Will run for ${duration} minutes, clicking random pins`);

    scheduleNextClick();

    // Update elapsed time every second
    elapsedIntervalRef.current = setInterval(() => {
      if (startTimeRef.current) {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setStats((prev) => ({ ...prev, elapsed }));
      }
    }, 1000);
  };

  const stopTest = (status: 'stopped' | 'completed' = 'stopped') => {
    if (testIntervalRef.current) {
      clearTimeout(testIntervalRef.current);
      testIntervalRef.current = null;
    }
    if (elapsedIntervalRef.current) {
      clearInterval(elapsedIntervalRef.current);
      elapsedIntervalRef.current = null;
    }
    setStats((prev) => ({ ...prev, status }));
    addLog(
      'info',
      `=== TEST ${status.toUpperCase()} === Total clicks: ${stats.clicks}, Errors: ${stats.errors}`
    );
  };

  const clearConsole = () => {
    setLogs([]);
    addLog('info', 'Console cleared');
  };

  // Capture console errors/warnings
  useEffect(() => {
    const originalConsoleError = console.error;
    const originalConsoleWarn = console.warn;

    console.error = function (...args) {
      originalConsoleError.apply(console, args);
      const message = args
        .map((a) => (typeof a === 'object' ? JSON.stringify(a) : String(a)))
        .join(' ');
      addLog('error', message);
      setStats((prev) => ({ ...prev, errors: prev.errors + 1 }));
    };

    console.warn = function (...args) {
      originalConsoleWarn.apply(console, args);
      const message = args
        .map((a) => (typeof a === 'object' ? JSON.stringify(a) : String(a)))
        .join(' ');
      addLog('warn', message);
      setStats((prev) => ({ ...prev, warnings: prev.warnings + 1 }));
    };

    // Capture unhandled errors
    const handleError = (e: ErrorEvent) => {
      addLog(
        'error',
        `Uncaught error: ${e.message} at ${e.filename}:${e.lineno}`
      );
      setStats((prev) => ({ ...prev, errors: prev.errors + 1 }));
    };

    const handleRejection = (e: PromiseRejectionEvent) => {
      addLog('error', `Unhandled promise rejection: ${e.reason}`);
      setStats((prev) => ({ ...prev, errors: prev.errors + 1 }));
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      console.error = originalConsoleError;
      console.warn = originalConsoleWarn;
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (testIntervalRef.current) clearTimeout(testIntervalRef.current);
      if (elapsedIntervalRef.current) clearInterval(elapsedIntervalRef.current);
    };
  }, []);

  return (
    <div className="relative h-screen w-screen">
      {/* Toggle Control Panel Button */}
      <button
        onClick={() => setShowControlPanel(!showControlPanel)}
        className="fixed top-4 right-4 z-[10001] bg-white rounded-lg shadow-lg p-3 hover:bg-gray-50 transition-colors"
        title={showControlPanel ? 'Hide Control Panel' : 'Show Control Panel'}
      >
        {showControlPanel ? (
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        ) : (
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        )}
      </button>

      {/* Control Panel */}
      {showControlPanel && (
        <div className="fixed top-0 left-0 right-0 bg-white shadow-lg z-[10000] p-4 max-h-[50vh] overflow-auto">
          <h2 className="text-xl font-bold mb-3 flex items-center gap-2">
            <span className="text-2xl">🧪</span>
            Mobile Pin Click Stress Test
          </h2>

          {/* Duration Selector */}
          <div className="mb-4 flex items-center gap-3">
            <label className="text-sm font-medium text-gray-700">
              Test Duration:
            </label>
            <select
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
              disabled={stats.status === 'running'}
              className="px-3 py-1.5 border border-gray-300 rounded-lg bg-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value={1}>1 minute</option>
              <option value={3}>3 minutes</option>
              <option value={5}>5 minutes</option>
              <option value={10}>10 minutes</option>
              <option value={15}>15 minutes</option>
              <option value={20}>20 minutes</option>
              <option value={30}>30 minutes</option>
            </select>
          </div>

          {/* Controls */}
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={startTest}
              disabled={stats.status === 'running'}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              Start Test ({duration} min)
            </button>
            <button
              onClick={() => stopTest('stopped')}
              disabled={stats.status !== 'running'}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              Stop Test
            </button>
            <button
              onClick={clearConsole}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
            >
              Clear Console
            </button>
          </div>

          {/* Progress Bar */}
          {stats.status === 'running' && (
            <div className="mb-4">
              <div className="flex justify-between text-xs text-gray-600 mb-1">
                <span>
                  {Math.floor(stats.elapsed / 60)}:
                  {String(stats.elapsed % 60).padStart(2, '0')}
                </span>
                <span>{stats.duration}:00</span>
              </div>
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-600 transition-all duration-1000"
                  style={{
                    width: `${Math.min(100, (stats.elapsed / (stats.duration * 60)) * 100)}%`,
                  }}
                />
              </div>
            </div>
          )}

          {/* Stats */}
          <div className="flex flex-wrap gap-4 text-sm mb-4 bg-gray-50 p-3 rounded-lg">
            <div>
              <strong>Status:</strong>{' '}
              <span
                className={
                  stats.status === 'running'
                    ? 'text-green-600'
                    : stats.status === 'completed'
                      ? 'text-blue-600'
                      : 'text-gray-600'
                }
              >
                {stats.status.charAt(0).toUpperCase() + stats.status.slice(1)}
              </span>
            </div>
            <div>
              <strong>Clicks:</strong> {stats.clicks}
            </div>
            <div>
              <strong>Elapsed:</strong> {Math.floor(stats.elapsed / 60)}m{' '}
              {stats.elapsed % 60}s
            </div>
            <div>
              <strong>Errors:</strong>{' '}
              <span
                className={stats.errors > 0 ? 'text-red-600 font-bold' : ''}
              >
                {stats.errors}
              </span>
            </div>
            <div>
              <strong>Warnings:</strong>{' '}
              <span className={stats.warnings > 0 ? 'text-orange-600' : ''}>
                {stats.warnings}
              </span>
            </div>
          </div>

          {/* Console Log */}
          <div className="bg-gray-900 text-green-400 p-3 rounded-lg font-mono text-xs max-h-48 overflow-auto">
            {logs.length === 0 ? (
              <div className="text-gray-500">
                Console output will appear here...
              </div>
            ) : (
              logs.map((log, idx) => (
                <div
                  key={idx}
                  className={
                    log.level === 'error'
                      ? 'text-red-400 font-bold'
                      : log.level === 'warn'
                        ? 'text-orange-400'
                        : 'text-green-400'
                  }
                >
                  [{log.timestamp}] [{log.level.toUpperCase()}] {log.message}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
